<template>
    <DForm
        :errors="errors"
        class="w-full flex flex-col items-center"
        @submit="submit"
    >
        <DField
            id="username"
            label="Username"
        >
            <DInput
                v-model="username"
                data-cy="username"
                autocorrect="off"
                autocapitalize="off"
            />
        </DField>
        <DField
            id="password"
            label="Password"
        >
            <DInput
                v-model="password"
                data-cy="password"
                type="password"
                autocorrect="off"
                autocapitalize="off"
                @keydown.enter="submit"
            />
        </DField>

        <div
            v-if="version.login_checkbox_html"
            class="w-full mt-5"
        >
            <DCheckbox
                v-model="checkboxChecked"
                checkbox-position="self-start"
            >
                <UIHtml
                    :html="version.login_checkbox_html"
                    class="break-words -mt-1 prose font-regular text-sm prose-strong:text-gray-600 text-gray-600 prose-a:text-[--color-brand-600] prose-a:font-regular"
                />
            </DCheckbox>
        </div>

        <DButton
            :loading="loading"
            :disabled="!canSignIn"
            data-cy="submit"
            size="lg"
            wide
            @click="submit"
        >
            Sign in
        </DButton>

        <template v-if="version.login_links">
            <template
                v-for="(link, index) in version.login_links"
                :key="index"
            >
                <div
                    v-if="link.link"
                    class="text-(--color-brand-600) text-sm font-medium underline mt-5 cursor-pointer"
                    @click="linkClicked(link)"
                >
                    {{ link.text }}
                </div>
            </template>
        </template>
    </DForm>
</template>

<script setup>
    import { useRouter } from 'vue-router'
    import { isAxiosError } from 'axios'
    import {
        DButton, DCheckbox, DField, DForm, DInput,
    } from '@digistorm/spark'
    import { usePlatformAuthStore } from '@/stores/platformAuth'
    import { usePlatformAuthBasicStore } from '@/stores/platformAuthBasic'

    const emit = defineEmits(['loading'])

    const props = defineProps({
        authRequirements: Object,
        loading: Boolean,
    })

    const router = useRouter()

    const platformAuthStore = usePlatformAuthStore()
    const platformAuthBasicStore = usePlatformAuthBasicStore()
    const versionStore = useVersionStore()
    const moduleHelpers = useModuleHelpers()
    const { homepageAnimation } = useAnimations()

    const { version } = storeToRefs(versionStore)

    const errors = ref(null)
    const submitting = ref(false)
    const checkboxChecked = ref(false)

    const username = ref('')
    const password = ref('')

    const url = computed(() => {
        return props.authRequirements.url
    })

    const canSignIn = computed(() => {
        const checkboxRequired = get(version.value, 'login_checkbox_html')
        if (!checkboxRequired) {
            return true
        }
        return checkboxChecked.value
    })

    const submit = () => {
        if (!canSignIn.value || submitting.value) {
            return
        }

        submitting.value = true
        emit('loading', true)
        errors.value = null

        platformAuthBasicStore.login({
            url: url.value,
            username: username.value,
            password: password.value,
        })
            .catch((e) => {
                // Ignore axios errors as they're handled via the error handler
                if (isAxiosError(e)) {
                    console.log(e)
                    return
                }
                throw e
            })
            .finally(() => {
                submitting.value = false
            })
    }

    const linkClicked = (link) => {
        if (!link.link) {
            return
        }

        moduleHelpers.getModuleOrOpenBrowserForLinkData(router, link.link)
            .then((appModule) => {
                if (!appModule || !appModule.id) {
                    return
                }
                router.push({ name: 'view', params: { id: appModule.id } }, homepageAnimation)
            })
    }

    const handleError = (e) => {
        if (get(e, 'response.status') === 422 && get(e, 'response.data.errors')) {
            errors.value = get(e, 'response.data.errors')
        }
    }

    onMounted(() => {
        platformAuthStore.events.on('error', handleError)
    })

    onUnmounted(() => {
        platformAuthStore.events.off('error', handleError)
    })
</script>
